import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Paper, Select, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatCurrency } from 'utils/format';
import { DataTableCell, DataTableHeadCell, DataTableValue } from 'components/shared/data-table/data-table.componet';
import DataTableTextField from 'components/shared/data-table/data-table-text-field.component';
import { PriceRequest, PriceRequestLine, PriceRequestLineOutcome } from 'models/price-request';
import { DeltaCell, DeltaPercentCell, HistoryButton, ProductCell } from './price-request-line-cells.component';
import { PriceField, PriceRequestResponse } from './use-price-request-response';

type PriceRequestResponseGridProps = {
    request: PriceRequest;
    response: PriceRequestResponse;
    onOpenHistory: (line: PriceRequestLine) => void;
};

const OUTCOMES = [
    PriceRequestLineOutcome.CHANGED,
    PriceRequestLineOutcome.UNCHANGED,
    PriceRequestLineOutcome.WITHDRAWN,
];

/**
 * The answers a line offers, with their label keys. A product that is already withdrawn can
 * only stay withdrawn or be offered again with a price; withdrawing it again changes nothing.
 */
const outcomeOptions = (line: PriceRequestLine) =>
    line.currentEnabled
        ? OUTCOMES.map((value) => ({ value, labelKey: `OUTCOME_ACTION_${value}` }))
        : [
              { value: PriceRequestLineOutcome.CHANGED, labelKey: 'OUTCOME_ACTION_OFFER_AGAIN' },
              { value: PriceRequestLineOutcome.UNCHANGED, labelKey: 'OUTCOME_ACTION_KEEP_WITHDRAWN' },
          ];

/** The typed price against today's, as shown while typing (the saved delta comes from the backend). */
const liveDelta = (typed: number | null, current: number | null | undefined) => {
    if (typed === null || current === null || current === undefined) return { delta: null, percent: null };
    const delta = typed - current;
    return { delta, percent: current ? Math.round((delta / current) * 1000) / 10 : null };
};

/**
 * DD-3697: the lab's response — per line change / leave unchanged / withdraw. Lines cannot be
 * added; the products are the ones the request was opened with.
 */
const PriceRequestResponseGrid: FC<PriceRequestResponseGridProps> = ({ request, response, onOpenHistory }) => {
    const { t: translate } = useTranslation('price-requests');

    // The compact, borderless cell editors of the order action grid (components/shared/data-table).
    const priceInput = (line: PriceRequestLine, field: PriceField, label: string, isEnabled: boolean) => (
        <DataTableTextField
            variant="standard"
            type="number"
            fullWidth
            alignRight
            autoComplete="off"
            readonly={!isEnabled}
            disabled={!isEnabled}
            error={isEnabled && response.isMissing(line, field)}
            required={isEnabled && response.isMissing(line, field)}
            InputProps={{ disableUnderline: true }}
            // No spin buttons: they crowd the narrow grid cells.
            sx={{
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    m: 0,
                },
                '& input[type=number]': { MozAppearance: 'textfield' },
            }}
            value={response.lines[line.action]?.[field] ?? ''}
            onChange={(event) => response.setValue(line.action, field, event.target.value)}
            inputProps={{
                min: field === 'deliveryTime' ? 1 : 0,
                step: field === 'deliveryTime' ? 1 : 'any',
                'aria-label': `${label} ${line.actionNumber ?? ''} ${line.actionName ?? ''}`.trim(),
                'data-test': `price-request.input-${field}-${line.action}`,
            }}
        />
    );

    return (
        <TableContainer component={Paper}>
            <Table size="small" data-test="price-request.response-grid">
                <TableHead>
                    <TableRow>
                        <DataTableHeadCell>{translate('PRODUCT')}</DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 110 }} alignRight>
                            {translate('CURRENT_PRICE')}
                        </DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 140 }}>{translate('ANSWER')}</DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 100 }} alignRight>
                            {translate('NEW_PRICE')}
                        </DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 90 }} alignRight>
                            {translate('CHANGE')}
                        </DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 80 }} alignRight>
                            {translate('CHANGE_PERCENT')}
                        </DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 100 }} alignRight>
                            {translate('TIOTANDVARD_PRICE')}
                        </DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 80 }} alignRight>
                            {translate('DELIVERY_DAYS')}
                        </DataTableHeadCell>
                        <DataTableHeadCell sx={{ width: 36, px: 0 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {request.lines.map((line) => {
                        const state = response.lines[line.action];
                        const outcome = state?.outcome ?? line.outcome;
                        const isChanged = outcome === PriceRequestLineOutcome.CHANGED;
                        const { delta, percent } = isChanged
                            ? liveDelta(response.toNumber(state?.price ?? ''), line.current.price)
                            : { delta: null, percent: null };
                        return (
                            <TableRow key={line._id} data-test={`price-request.response-${line.action}`}>
                                <DataTableCell readonly>
                                    <DataTableValue>
                                        <ProductCell line={line} />
                                    </DataTableValue>
                                </DataTableCell>
                                <DataTableCell readonly>
                                    <DataTableValue alignRight>
                                        {formatCurrency(line.current.price, request.currency) || '–'}
                                    </DataTableValue>
                                </DataTableCell>
                                <DataTableCell>
                                    <Select
                                        variant="standard"
                                        disableUnderline
                                        fullWidth
                                        value={outcome}
                                        onChange={(event) =>
                                            response.setOutcome(line, event.target.value as PriceRequestLineOutcome)
                                        }
                                        SelectDisplayProps={
                                            {
                                                'aria-label':
                                                    `${translate('ANSWER')} ${line.actionNumber ?? ''}`.trim(),
                                                'data-test': `price-request.outcome-${line.action}`,
                                            } as React.HTMLAttributes<HTMLDivElement>
                                        }
                                        sx={{ fontSize: '0.9rem', '& .MuiSelect-select': { py: '4px', pl: 1 } }}
                                    >
                                        {outcomeOptions(line).map(({ value, labelKey }) => (
                                            <MenuItem key={value} value={value}>
                                                {translate(labelKey)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </DataTableCell>
                                <DataTableCell readonly={!isChanged}>
                                    {priceInput(line, 'price', translate('NEW_PRICE'), isChanged)}
                                </DataTableCell>
                                <DataTableCell readonly>
                                    <DataTableValue alignRight>
                                        <DeltaCell delta={delta} currency={request.currency} />
                                    </DataTableValue>
                                </DataTableCell>
                                <DataTableCell readonly>
                                    <DataTableValue alignRight>
                                        <DeltaPercentCell delta={delta} percent={percent} />
                                    </DataTableValue>
                                </DataTableCell>
                                <DataTableCell readonly={!isChanged}>
                                    {priceInput(line, 'tiotandvardPrice', translate('TIOTANDVARD_PRICE'), isChanged)}
                                </DataTableCell>
                                <DataTableCell readonly={!isChanged}>
                                    {priceInput(line, 'deliveryTime', translate('DELIVERY_DAYS'), isChanged)}
                                </DataTableCell>
                                <DataTableCell sx={{ textAlign: 'center', width: 36 }}>
                                    <HistoryButton line={line} onOpen={onOpenHistory} />
                                </DataTableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PriceRequestResponseGrid;
